<template>
  <section :aria-labelledby="`${panelId}-heading`" data-test="guide-map-panel">
    <div class="max-w-3xl">
      <h2
        :id="`${panelId}-heading`"
        class="text-2xl text-black dark:text-white"
      >
        {{ $t("pages.guide.recommendationsMap") }}
      </h2>
    </div>

    <div class="mt-6 flex flex-wrap gap-3">
      <button
        v-if="googleMapsEmbedUrl"
        type="button"
        class="flex min-h-12 w-full cursor-pointer items-center justify-center gap-3 rounded-md bg-gold px-5 py-3 text-center text-sm font-bold text-black transition-colors hover:bg-black hover:text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black sm:w-fit dark:hover:bg-white dark:hover:text-black dark:focus-visible:outline-gold"
        data-test="guide-map-enlarge"
        @click="openEnlargedMap"
      >
        <Icon icon="marker-pin" class="text-lg" aria-hidden="true" />
        {{ $t("pages.guide.recommendationsEnlargeMap") }}
      </button>
      <a
        v-if="googleMapsOpenUrl"
        :href="googleMapsOpenUrl"
        :aria-describedby="googleMapsHintId"
        target="_blank"
        rel="noopener noreferrer"
        class="flex min-h-12 w-full cursor-pointer items-center justify-center gap-3 rounded-md border border-light-300 px-5 py-3 text-center text-sm font-bold text-black transition-colors hover:bg-light-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black sm:w-fit dark:border-grey-dark dark:text-white dark:hover:bg-grey-dark dark:focus-visible:outline-gold"
        data-test="guide-map-open-google"
      >
        {{ $t("pages.guide.recommendationsOpenInGoogleMaps") }}
      </a>
    </div>

    <p
      v-if="googleMapsOpenUrl"
      :id="googleMapsHintId"
      class="mt-2 max-w-3xl text-sm text-grey-dark dark:text-light-300"
    >
      {{ $t("pages.guide.recommendationsGoogleMapsCloseHint") }}
    </p>

    <div
      v-if="googleMapsEmbedUrl"
      class="mt-6 h-[60dvh] max-h-[760px] min-h-96 w-full overflow-hidden rounded-base border border-light-200 bg-light-100 dark:border-grey-dark dark:bg-dark"
      data-test="guide-map-embed"
    >
      <iframe
        :src="googleMapsEmbedUrl"
        :title="
          $t('pages.guide.recommendationsMapFrameTitle', {
            city: mapCityName,
          })
        "
        class="h-full w-full border-0"
        allowfullscreen
        loading="lazy"
        referrerpolicy="no-referrer"
      ></iframe>
    </div>

    <dialog
      v-if="googleMapsEmbedUrl"
      ref="enlargedMapDialog"
      class="fixed inset-0 m-0 h-dvh max-h-none w-screen max-w-none border-0 bg-white p-0 text-black dark:bg-black dark:text-white"
      :aria-labelledby="enlargedMapTitleId"
      data-test="guide-map-dialog"
      @close="enlargedMapOpen = false"
    >
      <div class="flex h-full flex-col">
        <header
          class="flex shrink-0 items-center justify-between gap-3 border-b border-light-200 px-4 py-3 dark:border-grey-dark"
        >
          <h2 :id="enlargedMapTitleId" class="text-base font-bold">
            {{
              $t("pages.guide.recommendationsMapFrameTitle", {
                city: mapCityName,
              })
            }}
          </h2>
          <button
            type="button"
            class="flex min-h-11 shrink-0 cursor-pointer items-center gap-2 rounded-md px-3 font-bold focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black dark:focus-visible:outline-gold"
            data-test="guide-map-close"
            @click="enlargedMapDialog?.close()"
          >
            <Icon icon="x-close" aria-hidden="true" />
            {{ $t("pages.guide.recommendationsCloseEnlargedMap") }}
          </button>
        </header>
        <iframe
          v-if="enlargedMapOpen"
          :src="googleMapsEmbedUrl"
          :title="
            $t('pages.guide.recommendationsMapFrameTitle', {
              city: mapCityName,
            })
          "
          class="min-h-0 w-full flex-1 border-0"
          allowfullscreen
          referrerpolicy="no-referrer"
          data-test="guide-map-enlarged-embed"
        ></iframe>
      </div>
    </dialog>
  </section>
</template>

<script lang="ts" setup>
import { computed, ref, useId } from "vue";
import type { Guide } from "@/types";
import {
  getGoogleMapsEmbedUrl,
  getGoogleMapsOpenUrl,
} from "@/utils/googleMaps";

const props = defineProps<{
  guide: Guide;
  panelId: string;
}>();

const enlargedMapDialog = ref<HTMLDialogElement | null>(null);
const enlargedMapOpen = ref(false);
const enlargedMapTitleId = useId();
const googleMapsHintId = useId();

const openEnlargedMap = () => {
  if (!enlargedMapDialog.value || enlargedMapDialog.value.open) return;
  enlargedMapDialog.value.showModal();
  enlargedMapOpen.value = true;
};

const googleMapsOpenUrl = computed(() =>
  getGoogleMapsOpenUrl(props.guide.google_maps_link),
);
const googleMapsEmbedUrl = computed(() =>
  getGoogleMapsEmbedUrl(props.guide.google_maps_link),
);

const primaryCity = computed(() => {
  const cities = props.guide.cities ?? [];
  return (
    cities.find((city) => city.id === props.guide.primary_city_id) ??
    cities.find((city) => city.name === props.guide.city) ??
    cities[0]
  );
});

const mapCityName = computed(
  () => props.guide.city?.trim() || primaryCity.value?.name || "Walkative",
);
</script>
